import {
    FileText,
    User,
    ShieldCheck,
    Briefcase,
    MapPin,
    Image as ImageIcon,
    FileCheck,
    ClipboardCheck,
    Building2,
    Mail,
    Phone,
    Calendar,
    ExternalLink,
    ScrollText,
    MessageSquare,
    AlertTriangle,
} from "lucide-react"

import Status from "../../Pages/PreCheck/Components/Status"
import LazyImageCard from "../../Pages/PreCheck/Components/LazyImageCard"

const formatDateTime = (date) => {
    if (!date) return null
    const parsed = new Date(date)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

const Field = ({ label, value }) => {
    if (value === null || value === undefined || value === "") return null
    return (
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="font-semibold">{value}</p>
        </div>
    )
}

// A candidate identifier (year of birth / last-4 SSN) that the client audience does not
// receive: the backend returns null for the client, and the field renders a muted "Redacted"
// marker (rather than disappearing) so it's clear the value is intentionally withheld. QA and
// the candidate get the real value and see it normally.
const RedactableField = ({ label, value }) => (
    <div>
        <p className="text-sm text-gray-500">{label}</p>
        {value !== null && value !== undefined && value !== ""
            ? <p className="font-semibold">{value}</p>
            : <p className="font-semibold text-gray-300 italic select-none">Redacted</p>}
    </div>
)

const SubSection = ({ title, icon: Icon, children }) => (
    <div className="border-t border-gray-100 pt-5 first:border-t-0 first:pt-0">
        <div className="flex items-center gap-2 mb-3">
            {Icon && <Icon className="w-4 h-4 text-gray-400" />}
            <p className="text-sm font-semibold text-gray-700">{title}</p>
        </div>
        {children}
    </div>
)

// A QA-controllable report value (work status / a side's "valid until"). Rendered as
// a card so the QA comment has room to breathe; when QA overrode the value an amber
// accent + "QA Adjusted" badge make that explicit instead of presenting it as
// document-derived. `value` is shown as-is (valid-until dates are plain YYYY-MM-DD
// strings, matching the per-document Valid Until display); an overridden side with no
// date falls back to `emptyValueLabel` ("No expiry").
const OverridableValueCard = ({ label, value, overridden, comment, emptyValueLabel = null }) => {
    const displayValue = value || (overridden ? emptyValueLabel : null)
    if (!displayValue) return null
    return (
        <div className={`rounded-lg border p-4 ${overridden ? "border-amber-200 bg-amber-50" : "border-gray-100 bg-gray-50"}`}>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{label}</p>
            <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-gray-900">{displayValue}</p>
                {overridden && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">
                        QA Adjusted
                    </span>
                )}
            </div>
            {comment && (
                <p className="flex items-start gap-1.5 text-xs text-gray-600 mt-2 leading-relaxed">
                    <MessageSquare className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
                    <span>{comment}</span>
                </p>
            )}
        </div>
    )
}

// requestor.address → single readable line, skipping any missing parts.
const formatAddress = (address) => {
    if (!address) return null
    const line = [
        address.addressLine1,
        address.addressLine2,
        address.city,
        [address.state, address.zipCode].filter(Boolean).join(" "),
        address.country,
    ]
        .filter(Boolean)
        .join(", ")
    return line || null
}

const Requestor = ({ requestor }) => {
    if (!requestor) {
        return <p className="text-sm text-gray-500">No requestor information.</p>
    }

    const address = formatAddress(requestor.address)

    return (
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-2">
            {/* Company name, then the company address, then the requesting user's name + email. */}
            {requestor.companyName && (
                <p className="font-semibold text-gray-800">{requestor.companyName}</p>
            )}
            {address && (
                <p className="flex items-start gap-1.5 text-sm text-gray-600">
                    <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                    <span>{address}</span>
                </p>
            )}
            {(requestor.name || requestor.email) && (
                <div className="pt-1 space-y-0.5">
                    {requestor.name && (
                        <p className="text-sm font-medium text-gray-700">{requestor.name}</p>
                    )}
                    {requestor.email && (
                        <a
                            href={`mailto:${requestor.email}`}
                            className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline"
                        >
                            <Mail className="w-3.5 h-3.5 shrink-0" />
                            {requestor.email}
                        </a>
                    )}
                </div>
            )}
        </div>
    )
}

// The consumer reporting agency (us) — static compliance block shown alongside the requestor
// on every report. Data comes from the backend (report.consumerReportingAgency).
const ConsumerReportingAgency = ({ agency }) => {
    if (!agency) return null

    const address = (agency.addressLines || []).filter(Boolean).join(", ")

    return (
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-2">
            {agency.name && <p className="font-semibold text-gray-800">{agency.name}</p>}
            {address && (
                <p className="flex items-start gap-1.5 text-sm text-gray-600">
                    <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                    <span>{address}</span>
                </p>
            )}
            {agency.phone && (
                <p className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Phone className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                    <span>{agency.phone}</span>
                </p>
            )}
            {agency.disputeEmail && (
                <a
                    href={`mailto:${agency.disputeEmail}`}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline"
                >
                    <Mail className="w-3.5 h-3.5 shrink-0" />
                    {agency.disputeEmail}
                </a>
            )}
        </div>
    )
}

// FCRA consent / summary-of-rights documents — open in a new tab.
const DisclosureLink = ({ label, url }) => {
    if (!url) return null
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200 bg-white text-blue-700 text-sm font-semibold hover:bg-blue-50 transition-colors"
        >
            <FileText className="w-4 h-4 shrink-0" />
            {label}
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
        </a>
    )
}

const DocumentsUsed = ({ documents, documentsUsed }) => {
    const labels = documentsUsed?.length
        ? documentsUsed
        : (documents || []).map((d) => d?.documentLabel).filter(Boolean)

    // No documents to show — hide the block entirely. This is also how the client/candidate
    // report withholds the used documents (they reveal the immigration class): the backend
    // returns them empty for those audiences.
    if (!labels.length) {
        return null
    }

    return (
        <div className="flex flex-wrap gap-2">
            {labels.map((label, i) => (
                <span
                    key={`${label}-${i}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700"
                >
                    <FileCheck className="w-3.5 h-3.5 text-gray-400" />
                    {label}
                </span>
            ))}
        </div>
    )
}

const QaReviewItems = ({ items }) => {
    if (!items?.length) {
        return <p className="text-sm text-gray-500">No QA review items.</p>
    }

    return (
        <div className="space-y-2">
            {items.map((item, i) => (
                <div
                    key={item.analyzeResultId ?? `${item.type}-${i}`}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                >
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800">{item.label}</p>
                        {item.comment && (
                            <p className="text-xs text-gray-500 mt-0.5">{item.comment}</p>
                        )}
                    </div>
                    <div className="shrink-0">
                        <Status status={item.result} />
                    </div>
                </div>
            ))}
        </div>
    )
}

const MediaGrid = ({ items }) => {
    if (!items?.length) {
        return <p className="text-sm text-gray-500">None available.</p>
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((item, i) => (
                <LazyImageCard
                    key={item.src || i}
                    label={item.label}
                    src={item.src}
                    type={item.type}
                    warning={item.warning}
                />
            ))}
        </div>
    )
}

// selfies is an array of Veriff sessions, each with a face URL — the candidate's face
// capture, the only image surfaced on the consumer report, and shown with NO moderation
// label. A non-flagged selfie is the original capture; a flagged one is already replaced
// by a clean face-only crop server-side (and omitted entirely when no clean crop exists),
// so the report never carries the flagged body or a warning.
const selfiesToItems = (selfies) =>
    (selfies || [])
        .filter((selfie) => selfie?.src)
        .map((selfie, i) => ({
            src: selfie.src,
            label: (selfies?.length || 0) > 1 ? `Selfie ${i + 1}` : "Selfie",
            warning: null,
        }))

const TruCheckConsumerReport = ({ data: report, isLoading, error }) => {
    if (isLoading && !report) {
        return (
            <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6">
                <p className="text-sm text-gray-500">Loading consumer report...</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="rounded-xl border-2 border-red-200 bg-red-50 p-6 text-red-700">
                Failed to load consumer report.
            </div>
        )
    }

    if (!report) return null

    const {
        referenceNumber,
        fullName,
        yearOfBirth,
        ssn,
        photoIdVerification,
        employabilityVerification,
        workStatus,
        geoLocation,
        selfies,
        qaReviewItems,
        requestor,
        consumerReportingAgency,
        consentUrl,
        fcraSummaryOfRightsUrl,
        orderDate,
        reportDate,
    } = report

    const countryName = geoLocation?.country?.name
    const countryCode = geoLocation?.country?.code

    const orderDateLabel = formatDateTime(orderDate)
    const reportDateLabel = formatDateTime(reportDate)
    const hasDisclosures = consentUrl || fcraSummaryOfRightsUrl

    // Overall QA status shown in the header — Review Required if any QA item needs review,
    // otherwise Clear (mirrors how the pre-check's overall result is derived).
    const overallQaStatus = (qaReviewItems || []).some((item) => item.result === "Review Required")
        ? "Review Required"
        : "Clear"

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">

            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-[#0047AB] via-sky-300 to-[#0047AB]" />

            {/* Branded report header — TruCheck logo + report title, with the consumer reporting
                agency (who produced this report) directly beneath them in the same header section. */}
            <div
                className="px-6 py-5 border-b border-slate-200/70"
                style={{ background: "linear-gradient(160deg, #ffffff 0%, #f0f5ff 100%)" }}
            >
                <div className="flex items-center gap-4 min-w-0">
                    <img
                        src="/trucheck.png"
                        alt="TruCheck"
                        className="h-16 w-auto object-contain shrink-0"
                    />
                    <h2 className="text-xl font-bold text-slate-900 leading-tight">
                        TruCheck Consumer Report
                    </h2>
                </div>

                {consumerReportingAgency && (
                    <div className="mt-4">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                            Consumer Reporting Agency
                        </p>
                        <ConsumerReportingAgency agency={consumerReportingAgency} />
                    </div>
                )}
            </div>

            {/* Meta strip — order / report dates on the left; reference number + overall status
                right-aligned, mirroring their previous placement. */}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 px-6 py-3 bg-slate-50 border-b border-slate-200/70">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                    {orderDateLabel && (
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-xs font-medium text-slate-500">Order Date</span>
                            <span className="text-sm font-semibold text-slate-800">{orderDateLabel}</span>
                        </div>
                    )}
                    {reportDateLabel && (
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-xs font-medium text-slate-500">Report Date</span>
                            <span className="text-sm font-semibold text-slate-800">{reportDateLabel}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-5 ml-auto">
                    {referenceNumber && (
                        <div className="text-right">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em]">
                                Reference Number
                            </p>
                            <p className="text-sm font-semibold text-slate-800 font-mono tracking-wide">
                                {referenceNumber}
                            </p>
                        </div>
                    )}
                    <div className="text-right">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em] mb-1">
                            Overall Status
                        </p>
                        <div className="flex justify-end">
                            <Status status={overallQaStatus} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Report body */}
            <div className="p-6 space-y-6">

                {/* REQUESTOR */}
                {requestor && (
                    <SubSection title="Requestor" icon={Building2}>
                        <Requestor requestor={requestor} />
                    </SubSection>
                )}

                {/* DISCLOSURES & CONSENT */}
                {hasDisclosures && (
                    <SubSection title="Disclosures & Consent" icon={ScrollText}>
                        <div className="flex flex-wrap gap-3">
                            <DisclosureLink label="Consent" url={consentUrl} />
                            <DisclosureLink label="FCRA Summary of Rights" url={fcraSummaryOfRightsUrl} />
                        </div>
                    </SubSection>
                )}

                {/* PERSONAL INFO */}
                <SubSection title="Personal Information" icon={User}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Full Name" value={fullName} />
                        {/* Redacted for the client (backend returns null); shown to QA + candidate. */}
                        <RedactableField label="Year of Birth" value={yearOfBirth} />
                    </div>
                </SubSection>

                {/* SSN */}
                <SubSection title="SSN" icon={ShieldCheck}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        {/* Redacted for the client (backend returns null); shown to QA + candidate. */}
                        <RedactableField
                            label="Last Four"
                            value={ssn?.lastFour ? `••• •• ${ssn.lastFour}` : null}
                        />
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Status</p>
                            <div className="flex items-center gap-1.5">
                                <Status status={ssn?.status} />
                                {/* Same explanation the TruCheck card shows — the SSN could not be
                                    verified through the commercial databases. Shown on hover. */}
                                {ssn?.status === "Cannot Be Validated" && (
                                    <div className="relative group/ssn flex items-center">
                                        <AlertTriangle className="w-4 h-4 text-amber-500 cursor-help shrink-0" />
                                        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover/ssn:block w-60 z-20 rounded-lg bg-gray-800 text-white text-[11px] leading-snug px-3 py-2 shadow-lg">
                                            Last 4 SSN could not be validated using commercial Databases. This does not necessarily mean that the SSN does not exist
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </SubSection>

                {/* PHOTO ID VERIFICATION */}
                <SubSection title="Photo ID Verification" icon={ShieldCheck}>
                    <div className="mb-3">
                        <Status status={photoIdVerification?.status} />
                    </div>
                    {/* Shown when a date exists, or when QA overrode the side (even to "no expiry"). */}
                    {(photoIdVerification?.validUntil || photoIdVerification?.validUntilOverridden) && (
                        <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <OverridableValueCard
                                label="Identity Valid Until"
                                value={photoIdVerification?.validUntil}
                                overridden={photoIdVerification?.validUntilOverridden}
                                comment={photoIdVerification?.validUntilComment}
                                emptyValueLabel="No expiry"
                            />
                        </div>
                    )}
                    <DocumentsUsed
                        documents={photoIdVerification?.documents}
                        documentsUsed={photoIdVerification?.documentsUsed}
                    />
                </SubSection>

                {/* EMPLOYABILITY VERIFICATION */}
                <SubSection title="Employability Verification" icon={Briefcase}>
                    <div className="mb-3">
                        <Status status={employabilityVerification?.status} />
                    </div>
                    {/* Work status + employability expiry, each with its QA override flag/comment.
                        Backend supplies the label — client/candidate get a non-revealing
                        "Employment Authorized in the US" / "Cannot Determine"; QA/ADMIN get the real
                        work status. Rendered the same way for all. */}
                    {(workStatus?.label || employabilityVerification?.validUntil || employabilityVerification?.validUntilOverridden) && (
                        <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <OverridableValueCard
                                label="Work Status"
                                value={workStatus?.label}
                                overridden={workStatus?.overridden}
                                comment={workStatus?.comment}
                            />
                            <OverridableValueCard
                                label="Employability Valid Until"
                                value={employabilityVerification?.validUntil}
                                overridden={employabilityVerification?.validUntilOverridden}
                                comment={employabilityVerification?.validUntilComment}
                                emptyValueLabel="No expiry"
                            />
                        </div>
                    )}
                    <DocumentsUsed
                        documents={employabilityVerification?.documents}
                        documentsUsed={employabilityVerification?.documentsUsed}
                    />
                </SubSection>

                {/* QA REVIEW */}
                <SubSection title="QA Review" icon={ClipboardCheck}>
                    <QaReviewItems items={qaReviewItems} />
                </SubSection>

                {/* GEO LOCATION */}
                <SubSection title="Geo Location" icon={MapPin}>
                    {geoLocation ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Field
                                label="Country"
                                value={countryName && countryCode ? `${countryName} (${countryCode})` : countryName || countryCode}
                            />
                            <Field label="State / Region" value={geoLocation?.region?.name} />
                            <Field label="City" value={geoLocation?.city?.name} />
                            <Field label="Continent" value={geoLocation?.continent?.name} />
                            <Field label="Timezone" value={geoLocation?.timezone?.name} />
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No geo location data available.</p>
                    )}
                </SubSection>

                {/* SELFIE — the candidate's face capture from the Veriff session
                    (un-watermarked). The only image surfaced on the consumer report. */}
                <SubSection title="Selfie" icon={ImageIcon}>
                    <MediaGrid items={selfiesToItems(selfies)} />
                </SubSection>

            </div>

            {/* COMPLIANCE FOOTER — dispute contact + FCRA rights. Shown on every report. */}
            <div className="border-t border-slate-200/70 bg-slate-50 px-6 py-5 space-y-3">
                <p className="text-xs leading-relaxed text-slate-600">
                    <span className="font-semibold text-slate-700">To File a Dispute:</span>{" "}
                    As a Candidate, please contact {consumerReportingAgency?.name || "VerifiedID LLC"} directly at{" "}
                    {consumerReportingAgency?.disputeEmail ? (
                        <a href={`mailto:${consumerReportingAgency.disputeEmail}`} className="text-blue-700 hover:underline">
                            {consumerReportingAgency.disputeEmail}
                        </a>
                    ) : "their compliance team"}. Their compliance team will investigate the processing
                    discrepancy at no cost to you.
                </p>
                <p className="text-xs leading-relaxed text-slate-600">
                    <span className="font-semibold text-slate-700">Review Your Rights:</span>{" "}
                    As a Candidate, you can view and download a copy of your federal consumer protections here:{" "}
                    {fcraSummaryOfRightsUrl ? (
                        <a href={fcraSummaryOfRightsUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                            A Summary of Your Rights Under the Fair Credit Reporting Act
                        </a>
                    ) : (
                        "A Summary of Your Rights Under the Fair Credit Reporting Act"
                    )}. This document is also permanently pinned inside your secure OTP portal dashboard.
                </p>
            </div>
        </div>
    )
}

export default TruCheckConsumerReport
